import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
import { MailIcon, RefreshCwIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { auth, db, actionCodeSettings } from '../../firebase';
import { sendEmailVerification } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
/**
 * Student Email Verification waiting screen.
 *
 * Behavior:
 *   - If the signed-in user is already verified (e.g. they verified in another
 *     tab, or returned to this tab after clicking the link), the page switches
 *     to a success state and auto-redirects to the appropriate destination.
 *   - Otherwise: shows confirmation + resend (60s cooldown). No manual
 *     "Go to sign in" CTA — the verification email link is the canonical path
 *     to the post-verification flow.
 *   - No polling. The verified check runs once on mount, and again whenever
 *     the tab becomes visible (covers the "verified in another tab" case).
 *   - Guests (no session) are bounced to /student-login.
 */
export const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const {
    currentUser
  } = useAuth();
  const [verificationSent, setVerificationSent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  // Where to send the user once we've confirmed verification. If they still
  // have an active authenticated session we send them straight to the
  // appropriate dashboard (avoiding a redundant sign-in step). Otherwise we
  // fall back to the student login page with the success banner.
  const [postVerifyDestination, setPostVerifyDestination] = useState<string>('/student-login?verified=true');
  // Refs to prevent duplicate work and avoid redirect loops.
  const hasRedirectedRef = useRef(false);
  const isCheckingRef = useRef(false);
  // Bounce guests away — this screen is only meaningful for a signed-in,
  // unverified user who just completed signup.
  useEffect(() => {
    if (!currentUser) {
      navigate('/student-login', {
        replace: true
      });
    }
  }, [currentUser, navigate]);
  // Detect verified state — runs on mount, when currentUser becomes
  // available, and when the tab regains visibility (covers the case where
  // the student verified in another tab and returned to this one).
  useEffect(() => {
    if (!currentUser) return;
    const checkVerified = async () => {
      if (isCheckingRef.current || hasRedirectedRef.current) return;
      isCheckingRef.current = true;
      try {
        const user = auth.currentUser;
        if (!user) return;
        await user.reload();
        const refreshed = auth.currentUser;
        if (refreshed && refreshed.emailVerified && !hasRedirectedRef.current) {
          hasRedirectedRef.current = true;
          let destination = '/student-login?verified=true';
          try {
            let resolvedUserType: string | null = null;
            try {
              const userDoc = await getDoc(doc(db, 'users', refreshed.uid));
              if (userDoc.exists()) {
                const data = userDoc.data() as any;
                if (typeof data?.userType === 'string') {
                  resolvedUserType = data.userType;
                }
              }
            } catch {
              // Non-fatal — fall through to localStorage
            }
            if (!resolvedUserType) {
              resolvedUserType = localStorage.getItem('userType');
            }
            if (resolvedUserType === 'teacher') {
              destination = '/teacher-dashboard';
            } else if (resolvedUserType === 'student') {
              destination = '/student-dashboard';
            }
          } catch (err) {
            console.warn('[EmailVerificationPage] destination resolution failed (non-fatal):', err);
          }
          setPostVerifyDestination(destination);
          setIsVerified(true);
          setTimeout(() => {
            navigate(destination, {
              replace: true
            });
          }, 1500);
        }
      } catch (err) {
        console.warn('[EmailVerificationPage] verified-state check failed (non-blocking):', err);
      } finally {
        isCheckingRef.current = false;
      }
    };
    checkVerified();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVerified();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUser, navigate]);
  // Resend cooldown timer — paused once verified.
  useEffect(() => {
    if (isVerified) return;
    if (!canResend && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      setCanResend(true);
    }
  }, [countdown, canResend, isVerified]);
  const handleResendVerification = async () => {
    if (!canResend) return;
    const user = auth.currentUser;
    if (!user) return;
    setResendError(null);
    try {
      await sendEmailVerification(user, actionCodeSettings);
      setVerificationSent(true);
      setCanResend(false);
      setCountdown(60);
      setTimeout(() => setVerificationSent(false), 5000);
    } catch (error: any) {
      console.error('[EmailVerificationPage] Error sending verification email:', error);
      if (error?.code === 'auth/too-many-requests') {
        setResendError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setResendError('Could not resend the verification email. Please try again shortly.');
      }
    }
  };
  // Success state — shown briefly before auto-redirect when we detect that
  // the account has already been verified.
  if (isVerified) {
    const goingToDashboard = postVerifyDestination.startsWith('/student-dashboard') || postVerifyDestination.startsWith('/teacher-dashboard');
    return <div className="w-full min-h-screen bg-white text-gray-900 font-sans" data-id="element-3940">
        <Navbar data-id="element-3941" />
        <main className="pt-20 pb-16" data-id="element-3942">
          <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-3943">
            <div className="bg-white border border-gray-200 rounded-2xl p-10 shadow-sm text-center" data-id="element-3944">
              <div className="bg-green-50 rounded-full p-3 w-16 h-16 mx-auto mb-6 flex items-center justify-center" data-id="element-3945">
                <CheckCircleIcon className="h-8 w-8 text-green-600" data-id="element-3946" />
              </div>
              <h1 className="text-2xl md:text-3xl font-serif font-semibold text-gray-900 mb-3" data-id="element-3947">
                Email verified
              </h1>
              <p className="text-base text-gray-600" data-id="element-3948">
                {goingToDashboard ? 'Your email has been verified. Taking you to your dashboard…' : 'Your email has been verified. Redirecting you to sign in…'}
              </p>
            </div>
          </div>
        </main>
        <Footer data-id="element-3949" />
      </div>;
  }
  return <div className="w-full min-h-screen bg-white text-gray-900 font-sans" data-id="element-3950">
      <Navbar data-id="element-3951" />
      <main className="pt-20 pb-16" data-id="element-3952">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-3953">
          <div className="bg-white border border-gray-200 rounded-2xl p-10 shadow-sm text-center" data-id="element-3954">
            <div className="bg-gray-100 rounded-full p-3 w-16 h-16 mx-auto mb-6 flex items-center justify-center" data-id="element-3955">
              <MailIcon className="h-8 w-8 text-gray-700" data-id="element-3956" />
            </div>

            <h1 className="text-2xl md:text-3xl font-serif font-semibold text-gray-900 mb-3" data-id="element-3957">
              Verify your email
            </h1>

            <p className="text-base text-gray-700 mb-2" data-id="element-3958">
              We've sent a verification link to{' '}
              <span className="font-medium text-gray-900" data-id="element-3959">
                {currentUser?.email}
              </span>
              .
            </p>
            <p className="text-sm text-gray-500 mb-8" data-id="element-3960">
              Click the link in your email to verify your account and continue.
            </p>

            {verificationSent && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center justify-center text-sm" data-id="element-3961">
                <CheckCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" data-id="element-3962" />
                Verification email resent successfully!
              </div>}

            {resendError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-start text-left" data-id="element-3963">
                <AlertCircleIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-3964" />
                <span className="text-sm" data-id="element-3965">{resendError}</span>
              </div>}

            <div className="flex items-center justify-center mb-8" data-id="element-3966">
              <button onClick={handleResendVerification} disabled={!canResend} className="border border-gray-300 hover:bg-gray-50 text-gray-800 font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center min-w-[220px] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3967">
                <RefreshCwIcon className="h-5 w-5 mr-2" data-id="element-3968" />
                {canResend ? 'Resend email' : `Resend in ${countdown}s`}
              </button>
            </div>

            <div className="text-gray-500 text-sm" data-id="element-3969">
              <p className="mb-2" data-id="element-3970">
                Don't see the email? Please check your Spam or Promotions
                folder.
              </p>
              <p data-id="element-3971">
                Need help?{' '}
                <a href="/contact" className="text-gray-900 underline hover:text-gray-700" data-id="element-3972">
                  Contact support
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-3973" />
    </div>;
};